import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reorder } from "framer-motion";
import { GripVertical, Loader2, Save } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function ColumnOrderDialog({ open, onClose, products, onSaved }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setItems(products.map((p) => ({ id: p.flower_id, name: p.name }))); }, [open, products]);

  const save = async () => {
    setSaving(true);
    try {
      const prev = await adminApi.getColumnOrder();
      const here = new Set(items.map((i) => i.id));
      const ids = [...items.map((i) => i.id), ...(prev.flower_ids || []).filter((f) => !here.has(f))];
      await adminApi.saveColumnOrder(ids);
      toast.success("Column order saved as default"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="column-order-dialog">
        <DialogHeader><DialogTitle>Column order</DialogTitle></DialogHeader>
        <p className="text-xs text-[#28282E]/70">Drag products into the order you want. This becomes the default layout for every report, PDF and SAP export.</p>
        <Reorder.Group axis="y" values={items} onReorder={setItems} className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-1">
          {items.map((it, i) => (
            <Reorder.Item key={it.id} value={it} data-testid={`column-order-item-${it.id}`}
              className="flex cursor-grab select-none items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-semibold text-[#28282E] active:cursor-grabbing">
              <GripVertical className="h-4 w-4 shrink-0 text-[#28282E]/40" /><span className="w-5 text-[#28282E]/40">{i + 1}</span><span className="truncate uppercase">{it.name || "(unnamed)"}</span>
            </Reorder.Item>
          ))}
        </Reorder.Group>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-200">Cancel</Button>
          <Button data-testid="column-order-save" onClick={save} disabled={saving} className="bg-[#28282E] text-white hover:bg-[#1F1F24]">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save as default</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
